import React, {useEffect} from "react";
import {EditorContent, useEditor} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {Button, Space, Tooltip} from "antd";
import {BoldOutlined, ItalicOutlined, OrderedListOutlined, UnorderedListOutlined} from "@ant-design/icons";
import DOMPurify from "dompurify";
import {marked} from "marked";
import "./style.css";

type Props = {
    value?: string;
    onChange?: (html: string) => void;
    placeholder?: string;
};

export default function RichText({value = "", onChange, placeholder}: Props) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                blockquote: false,
                code: false,
                codeBlock: false,
                horizontalRule: false,
                strike: false,
            }),
        ],
        content: value || "",
        editorProps: {
            attributes: {
                class: "rtx-editor",
                "data-placeholder": placeholder || "Write a description...",
            },

            handlePaste(view, event) {
                const clipboard = event.clipboardData;
                if (!clipboard) return false;

                const html = clipboard.getData("text/html");
                const text = clipboard.getData("text/plain");

                if (html && html.trim().length > 0) return false;

                const looksLikeMarkdown =
                    /(^|\n)\s*([-*+])\s+/.test(text) ||         // - item
                    /(^|\n)\s*\d+\.\s+/.test(text) ||          // 1. item
                    /\*\*[^*]+\*\*/.test(text) ||              // **bold**
                    /(^|[^*])\*[^*]+\*/.test(text);            // *italic*

                if (!looksLikeMarkdown) return false;

                const mdHtml = marked.parse(text, {breaks: true}) as string;

                const safe = DOMPurify.sanitize(mdHtml, {
                    ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "ul", "ol", "li"],
                    ALLOWED_ATTR: [],
                });

                event.preventDefault();
                editor?.chain().focus().insertContent(safe).run();
                return true;
            },
        },
        onUpdate: ({editor}) => onChange?.(editor.getHTML()),
    });


    useEffect(() => {
        if (!editor) return;
        const next = value || "";
        const current = editor.getHTML();
        if (next !== current) editor.commands.setContent(next, {emitUpdate: false});
    }, [value, editor]);

    if (!editor) return null;

    const btnType = (active: boolean) => (active ? "primary" : "default");

    return (
        <div className="rtx-wrap">
            <Space.Compact className="rtx-toolbar" block>
                <Tooltip title="Bold">
                    <Button
                        type={btnType(editor.isActive("bold"))}
                        icon={<BoldOutlined/>}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                    />
                </Tooltip>
                <Tooltip title="Italic">
                    <Button
                        type={btnType(editor.isActive("italic"))}
                        icon={<ItalicOutlined/>}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                    />
                </Tooltip>
                <Tooltip title="Bulleted list">
                    <Button
                        type={btnType(editor.isActive("bulletList"))}
                        icon={<UnorderedListOutlined/>}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                    />
                </Tooltip>
                <Tooltip title="Numbered list">
                    <Button
                        type={btnType(editor.isActive("orderedList"))}
                        icon={<OrderedListOutlined/>}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    />
                </Tooltip>
            </Space.Compact>

            <div className="rtx-content">
                <EditorContent editor={editor}/>
            </div>
        </div>
    );
}